import { ClickOutside } from "@dropbox/dig-components/click_outside";
import { useTheme } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  AddCircleLine,
  DropboxLine,
  StacksFill,
} from "@dropbox/dig-icons/dist/mjs/assets";
import { GlyphLogo } from "@dropbox/dig-logos";
import emojiMartData from "@emoji-mart/data";
import i18n from "@mirage/translations";
import { lazy, Suspense, useMemo } from "react";
import { renderToString } from "react-dom/server";
import styles from "./EmojiPicker.module.css";

const Picker = lazy(() => import("@emoji-mart/react"));

const CATEGORIES = [
  "frequent",
  "custom_logo",
  "dropbox",
  "people",
  "nature",
  "foods",
  "activity",
  "places",
  "objects",
  "symbols",
  "flags",
];

// EmojiData is returned from the emoji picker.
export type EmojiData = {
  id: string;
  name: string;
  native: string;
  unified: string;
  keywords: string[];
  shortcodes: string[];
  skin?: number;
  src?: string;
  aliases?: string[];
  emoticons?: string[];
};

export const EmojiPicker = ({
  onCancel,
  onSelect,
  onUpload,
}: {
  onCancel: () => void;
  onSelect: (emoji: EmojiData) => void;
  onUpload?: () => void;
}) => {
  const theme = useTheme();

  const categoryIcons = useMemo(() => {
    const dropboxIcon = renderToString(
      <GlyphLogo
        size={32}
        src={DropboxLine}
        xmlns="http://www.w3.org/2000/svg"
      />,
    );

    return { dropbox: { svg: dropboxIcon } };
  }, []);

  const handleEmojiSelect = (emoji: EmojiData) => {
    if (emoji.id === "logo" && onUpload) {
      onUpload();
      return false;
    }
    onSelect(emoji);
  };

  const customIcons = useMemo(() => {
    const stackIconSvgSrc = renderToString(<UIIcon src={StacksFill} />)
      // This is needed to make the SVG render correctly in an img tag
      .replace("<svg ", '<svg xmlns="http://www.w3.org/2000/svg" ')
      // This is needed to define an explicit color for the SVG
      .replace("currentColor", "%23736C64");

    const addCircleSvgSrc = renderToString(<UIIcon src={AddCircleLine} />)
      // This is needed to make the SVG render correctly in an img tag
      .replace("<svg ", '<svg xmlns="http://www.w3.org/2000/svg" ')
      // This is needed to define an explicit color for the SVG
      .replace("currentColor", "%23736C64");

    // custom emojis do not have a "native" representation, and so wind up clearing
    // the emoji field when selected. This is fine for the purposes of the default
    // emoji, but will need to be addressed if we want to add more.
    return [
      onUpload && {
        id: "custom_logo",
        name: i18n.t("stack_emoji_picker_custom_logo"),
        emojis: [
          {
            id: "logo",
            name: i18n.t("stack_emoji_picker_custom_logo"),
            skins: [
              {
                src: `data:image/svg+xml;charset=utf-8,${addCircleSvgSrc}`,
              },
            ],
          },
        ],
      },
      {
        id: "dropbox",
        name: "Dropbox",
        emojis: [
          {
            id: "stack",
            name: "Stack Icon",
            keywords: ["stack", "dash", "clear", "delete", "reset"],
            skins: [
              {
                src: `data:image/svg+xml;charset=utf-8,${stackIconSvgSrc}`,
              },
            ],
          },
        ],
      },
    ].filter(Boolean);
  }, [onUpload]);

  return (
    <ClickOutside
      isActive
      isClickThroughPortaled={false}
      onClickOutside={onCancel}
    >
      <div className={styles.container}>
        <Suspense fallback={<></>}>
          <Picker
            categories={CATEGORIES}
            categoryIcons={categoryIcons}
            custom={customIcons}
            data={emojiMartData}
            onEmojiSelect={handleEmojiSelect}
            theme={theme.mode === "dark" ? "dark" : "light"}
          />
        </Suspense>
      </div>
    </ClickOutside>
  );
};
